<script lang="ts">
  import type { App } from '$lib/types';
  import { getContext } from 'svelte';
  import { BREAKPOINTS, STATIC_URL } from '$lib/constants';
  import { searchWithMeili } from '$lib/utils/search';

  import Meta from '$components/Meta.svelte';
  import Image from '$mds/Image/Image.svelte';
  import GamePoster from '$components/GamePoster.svelte';

  const { isLoggedIn } = getContext<App>('app');

  $: isMobile = innerWidth < BREAKPOINTS.md;

  let innerWidth: number;
  let innerHeight: number;
  let depth: string[] = Array(3).fill('');

  function parallax({ clientX, clientY }) {
    if (isMobile) return;

    let width = innerWidth / 2;
    let height = innerHeight / 2;
    depth[0] = `--tw-translate-x: ${(clientX - width) * 0.005}%; --tw-translate-y: ${(clientY - height) * 0.005}%`;
    depth[1] = `--tw-translate-x: ${(clientX - width) * 0.015}%; --tw-translate-y: ${(clientY - height) * 0.015}%`;
    depth[2] = `--tw-translate-x: ${(clientX - width) * 0.03}%; --tw-translate-y: ${(clientY - height) * 0.03}%`;
  }

  let searchPromise = searchWithMeili('games', '', { hitsPerPage: 7 });
</script>

<Meta title="Welcome to Room 404!" />

<svelte:window on:mousemove={parallax} bind:innerWidth bind:innerHeight />

<main class="overflow-hidden">
  <div
    class="container flex min-h-[calc(100vh-64px)] flex-col items-center justify-center pt-10 text-center"
    class:sm:min-h-screen={$isLoggedIn}
  >
    <div class="mt-auto flex flex-col items-center justify-center">
      <h1 class="text-display-md text-ondark-primary mb-6">
        <span>Oh hey!</span>
        <br class="md:hidden" />
        <span>Welcome to Room 404!</span>
      </h1>

      <div class="max-w-3xl">
        <p class="text-ondark-secondary mb-6 md:text-xl md:font-medium">
          I don't know what it was used for before, but now it's where I replenish my physical form by eating players
          who fall off the Smash Bros stages. I mean sleep.
        </p>

        <p class="text-ondark-secondary md:text-xl md:font-medium">
          Meaty is way higher level than you. Click one of these to grind some XP
        </p>
      </div>

      <div class="-mx-4 mb-16 mt-10 overflow-hidden md:mx-0">
        <div class="-mx-32 flex flex-wrap items-center justify-center md:mx-0">
          {#await searchPromise}
            <div class="spinner h-12 w-12" />
          {:then value}
            {#each value.hits as game (game.id)}
              <a
                href="/{game.slug}"
                class="bg-ondark-low hover:bg-ondark-high mr-4 mt-4 flex items-center space-x-2 rounded-lg p-2 pr-4 transition-colors lg:space-x-5 lg:pr-6"
              >
                <GamePoster {game} link={false} class="size-6 shrink-0 lg:size-12" rounded="rounded md:rounded-lg" />
                <span class="text-ondark-primary truncate whitespace-nowrap text-sm lg:text-xl">
                  {game.title.en}
                </span>
              </a>
            {/each}
          {/await}
        </div>
      </div>
    </div>

    <div class="-mx-4 mt-auto overflow-hidden md:hidden">
      <div class="-mx-24">
        <Image src="{STATIC_URL}/illustrations/grave.svg" class="w-[854px]" lazy={false} />
      </div>
    </div>

    <div class="pointer-events-none relative -mx-4 mt-auto hidden w-full pt-[40.62%] md:block">
      <div class="translate-y-1/5 absolute bottom-0 left-0 w-full">
        <div
          class="ease-smooth-out w-full translate-x-0 translate-y-0 transform-gpu transition-transform duration-700"
          style={depth[0]}
        >
          <Image src="{STATIC_URL}/illustrations/grave_background.svg" class="w-full" lazy={false} />
        </div>
      </div>

      <div class="translate-y-1/5 absolute bottom-0 left-1/2 w-[45.43%] -translate-x-1/2">
        <div
          class="ease-smooth-out w-full translate-x-0 translate-y-0 transform-gpu transition-transform duration-500"
          style={depth[1]}
        >
          <Image
            src="{STATIC_URL}/illustrations/grave_ground.svg"
            class="w-full translate-x-0 translate-y-0 transform-gpu transition-transform"
            lazy={false}
          />
        </div>
      </div>
      <div class="absolute left-1/2 top-0 w-[24.59%] -translate-x-1/2">
        <div
          class="ease-smooth-out w-full translate-x-0 translate-y-0 transform-gpu transition-transform duration-700"
          style={depth[2]}
        >
          <Image src="{STATIC_URL}/illustrations/grave_meaty.svg" class="w-full" lazy={false} />
        </div>
      </div>
    </div>
  </div>
</main>
